import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import AverageIcon from './average';
import {
    faBatteryThreeQuarters as solidBatteryThreeQuarters,
    faAlarmClock as solidAlarmClock,
    faAnalytics as solidAnalytics,
    faAnglesLeft as solidAnglesLeft,
    faAnglesRight as solidAnglesRight,
    faBell as solidBell,
    faBookmark as solidBookmark,
    faBolt as solidBolt,
    faBug as solidBug,
    faBuilding as solidBuilding,
    faCalendar as solidCalendar,
    faCalendarStar as solidCalendarStar,
    faChartColumn as solidChartColumn,
    faChartFft as solidChartFft,
    faChartLine as solidChartLine,
    faChartMixed as solidChartMixed,
    faChartPie as solidChartPie,
    faCheck as solidCheck,
    faChevronUp as solidChevronUp,
    faChevronDown as solidChevronDown,
    faChevronLeft as solidChevronLeft,
    faChevronRight as solidChevronRight,
    faClone as solidClone,
    faCircle as solidCircle,
    faCircleArrowUp as solidCircleArrowUp,
    faCircleCheck as solidCircleCheck,
    faCircleChevronRight as solidCircleChevronRight,
    faCircleEuro as solidCircleEuro,
    faCircleExclamation as solidCircleExclamation,
    faCircleInfo as solidCircleInfo,
    faCircleXmark as solidCircleXmark,
    faCircleLeft as solidCircleLeft,
    faClock as solidClock,
    faCloudRain as solidCloudRain,
    faCubes as solidCubes,
    faDatabase as solidDatabase,
    faDivide as solidDivide,
    faDiagramLeanCanvas as solidDiagramLeanCanvas,
    faDroplet as solidDroplet,
    faEllipsis as solidEllipsis,
    faExchange as solidExchange,
    faEye as solidEye,
    faEyeSlash as solidEyeSlash,
    faFileArrowUp as solidFileArrowUp,
    faFileContract as solidFileContract,
    faFileExport as solidFileExport,
    faFileSignature as solidFileSignature,
    faFilter as solidFilter,
    faFireBurner as solidFireBurner,
    faFlask as solidFlask,
    faFolderGear as solidFolderGear,
    faGaugeHigh as solidGaugeHigh,
    faGlobe as solidGlobe,
    faHashtag as solidHashtag,
    faKey as solidKey,
    faLayerGroup as solidLayerGroup,
    faLock as solidLock,
    faMagnifyingGlass as solidMagnifyingGlass,
    faMagnet as solidMagnet,
    faMarker as solidMarker,
    faMessage as solidMessage,
    faMicrochip as solidMicrochip,
    faNetworkWired as solidNetworkWired,
    faOilWell as solidOilWell,
    faPaintbrushPencil as solidPaintbrushPencil,
    faPen as solidPen,
    faPickaxe as solidPickaxe,
    faPlus as solidPlus,
    faRectangleVerticalHistory as solidRectangleVerticalHistory,
    faRightLeftLarge as solidRightLeftLarge,
    faArrowsRotate as solidArrowsRotate,
    faSnowman as solidSnowman,
    faServer as solidServer,
    faSmog as solidSmog,
    faSigma as solidSigma,
    faSun as solidSun,
    faTags as solidTags,
    faTableColumns as solidTableColumns,
    faTemperatureArrowDown as solidTemperatureArrowDown,
    faTemperatureArrowUp as solidTemperatureArrowUp,
    faToggleOff as solidToggleOff,
    faToggleOn as solidToggleOn,
    faTrash as solidTrash,
    faUmbrellaBeach as solidUmbrellaBeach,
    faUserPen as solidUserPen,
    faUsers as solidUsers,
    faUsersBetweenLines as solidUsersBetweenLines,
    faWaveSquare as solidWaveSquare,
    faWeightHanging as solidWeightHanging,
    faXmark as solidXmark
} from '@fortawesome/pro-solid-svg-icons';
import { faBookmark as faSharpBookmark } from '@fortawesome/sharp-solid-svg-icons';
import { cva } from 'class-variance-authority';
import { cn } from '../cn';

// Five public size stops. The progression covers inline glyphs, chrome
// accents, section headers, decorated badges, and hero / empty-state
// slots. The `x-large` step is the only arbitrary value in the scale —
// Tailwind has no native font-size between text-2xl (24px) and text-5xl
// (48px) that lands at 32px, but 32px sits at a meaningful spot in the
// scale (the gap between large and display would otherwise be 24px).
// The arbitrary value is centralised here once; consumers only ever say
// `size="x-large"`, never `tw-text-[32px]` directly.
//
// Tones mirror Typography.tone so an icon paired with text speaks the
// same colour vocabulary, plus the brand pair (`primary`, `secondary`)
// for icon-led UI (hero / empty-state) where the icon — not surrounding
// copy — carries the colour.
const iconStyles = cva('tw-inline-flex tw-leading-none', {
    variants: {
        size: {
            small: 'tw-text-sm',          // 14px — inline with body / labels
            medium: 'tw-text-lg',         // 18px — default; pairs with controls
            large: 'tw-text-2xl',         // 24px — section accents
            'x-large': 'tw-text-[32px]',  // 32px — decorated badges / shells
            display: 'tw-text-5xl'        // 48px — hero / empty-state
        },
        tone: {
            inherit: 'tw-text-current',
            foreground: 'tw-text-foreground',
            muted: 'tw-text-muted-foreground',
            background: 'tw-text-background',
            primary: 'tw-text-primary',
            secondary: 'tw-text-secondary',
            info: 'tw-text-info',
            success: 'tw-text-success',
            warning: 'tw-text-warning',
            destructive: 'tw-text-destructive'
        }
    },
    defaultVariants: {
        size: 'medium',
        tone: 'inherit'
    }
});

const iconFamilyMap = {
    custom: {
        average: AverageIcon
    },
    sharp: {
        bookmark: faSharpBookmark
    },
    solid: {
        // The dictionary maps slugs to FontAwesome glyph imports. Slugs
        // are the WM canonical name — pick one per glyph; do not add
        // aliases. Semantic redirection (e.g. "the destructive icon")
        // belongs in semanticFeedback.js, not here.
        alarm: solidAlarmClock,
        apiKeys: solidKey,
        arrowLeftCircle: solidCircleLeft,
        arrowUpCircle: solidCircleArrowUp,
        actuators: solidToggleOn,
        battery: solidBatteryThreeQuarters,
        bookmark: solidBookmark,
        bolt: solidBolt,
        buckets: solidDatabase,
        calendar: solidCalendar,
        carbonDioxide: solidSmog,
        chartColumn: solidChartColumn,
        chartFft: solidChartFft,
        chartLine: solidChartLine,
        chartMixed: solidChartMixed,
        chartPie: solidChartPie,
        change: solidExchange,
        check: solidCheck,
        circle: solidCircle,
        circleCheck: solidCircleCheck,
        circleChevronRight: solidCircleChevronRight,
        circleEuro: solidCircleEuro,
        circleInfo: solidCircleInfo,
        circleXmark: solidCircleXmark,
        chevronDown: solidChevronDown,
        chevronLeft: solidChevronLeft,
        chevronRight: solidChevronRight,
        chevronUp: solidChevronUp,
        chevronsLeft: solidAnglesLeft,
        chevronsRight: solidAnglesRight,
        clock: solidClock,
        cooling: solidTemperatureArrowDown,
        circuits: solidNetworkWired,
        close: solidXmark,
        contracts: solidFileSignature,
        dataCenter: solidServer,
        delete: solidTrash,
        density: solidCubes,
        devices: solidMicrochip,
        divide: solidDivide,
        diagramLeanCanvas: solidDiagramLeanCanvas,
        duplicate: solidClone,
        edit: solidPen,
        ellipsis: solidEllipsis,
        errors: solidBug,
        etls: solidRightLeftLarge,
        events: solidCalendarStar,
        exclamationCircle: solidCircleExclamation,
        eye: solidEye,
        eyeSlash: solidEyeSlash,
        fileExport: solidFileExport,
        filter: solidFilter,
        flask: solidFlask,
        frequency: solidWaveSquare,
        gas: solidFireBurner,
        globe: solidGlobe,
        groups: solidUsersBetweenLines,
        heating: solidTemperatureArrowUp,
        holidays: solidUmbrellaBeach,
        humidity: solidCloudRain,
        indicators: solidAnalytics,
        linkedIn: faLinkedin,
        lock: solidLock,
        magnifyingGlass: solidMagnifyingGlass,
        magnet: solidMagnet,
        markers: solidMarker,
        mass: solidWeightHanging,
        notifications: solidBell,
        oil: solidOilWell,
        password: solidKey,
        paintBrush: solidPaintbrushPencil,
        plans: solidLayerGroup,
        plus: solidPlus,
        preferences: solidFolderGear,
        pressure: solidGaugeHigh,
        rebuild: solidPickaxe,
        rectangleVerticalHistory: solidRectangleVerticalHistory,
        refresh: solidArrowsRotate,
        reports: solidFileContract,
        sites: solidBuilding,
        sigma: solidSigma,
        sun: solidSun,
        tableColumns: solidTableColumns,
        tags: solidTags,
        tariffs: solidTags,
        toggleOff: solidToggleOff,
        toggleOn: solidToggleOn,
        upload: solidFileArrowUp,
        user: solidUserPen,
        users: solidUsers,
        value: solidHashtag,
        water: solidDroplet
    }
};

// FontAwesome's solid family ships glyphs at slightly different visual
// weights — a handful feel oversized next to peers in toolbars and
// empty-states. The scale map nudges those outliers back into balance
// without changing the size variant. Add an entry here when an icon
// "feels too big" or "too small" against its siblings; never reach for
// inline `style={{ transform: ... }}` from a consumer.
const iconScaleMap = {
    custom: {
        average: 1.4
    },
    sharp: {},
    solid: {
        alarm: 0.9,
        battery: 0.9,
        notifications: 0.9,
        buckets: 0.8,
        reports: 0.82,
        sites: 0.78
    }
};

const resolveIcon = ({ family = 'solid', icon }) => {
    const resolvedIcon = iconFamilyMap[family]?.[icon];

    if (resolvedIcon) {
        return {
            family,
            value: resolvedIcon
        };
    }

    console.error(`[ui/Icon] Unknown icon "${String(icon)}" for family "${family}". Falling back to "snowman".`);

    return {
        family: 'solid',
        value: solidSnowman
    };
};

const Icon = ({ className: _className, family = 'solid', icon, size, style: _style, tone, ...props }) => {
    const resolvedIcon = resolveIcon({ family, icon });
    const iconClassName = cn(iconStyles({ size, tone }));
    const scale = iconScaleMap[family]?.[icon] ?? 1;
    const scaleStyle = scale === 1
        ? undefined
        : { transform: `scale(${scale})`, transformOrigin: 'center' };

    if (resolvedIcon.family === 'custom') {
        const SvgComponent = resolvedIcon.value;

        return (
            <span className={iconClassName} style={scaleStyle} {...props}>
                <SvgComponent width="1em" height="1em" />
            </span>
        );
    }

    return (
        <FontAwesomeIcon
            className={iconClassName}
            icon={resolvedIcon.value}
            style={scaleStyle}
            {...props}
        />
    );
};

Icon.displayName = 'Icon';

export { Icon };
